(() => {
  const instances = new WeakMap();
  function initialize(root) {
    root.querySelectorAll('[data-lt-look]').forEach(section => {
      if (instances.has(section)) return;
      const dialog = section.querySelector('[data-look-dialog]');
      const sheet = section.querySelector('[data-look-sheet]');
      const details = [...section.querySelectorAll('[data-look-detail]')];
      const dots = [...section.querySelectorAll('[data-look-dot]')];
      const triggers = [...section.querySelectorAll('[data-look-open]')];
      const controller = new AbortController(), options = { signal: controller.signal };
      const preload = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          details.forEach(detail => { detail.querySelector('img').loading = 'eager'; });
          preload.disconnect();
        }
      }, { rootMargin: '200px 0px' }) : null;
      preload?.observe(section);
      const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
      let current = 0, opener = null, motion = null, closing = false, locks = [], touch = null, revision = 0;
      function show(index) {
        const request = ++revision;
        const changing = dialog.open;
        current = (index + details.length) % details.length;
        details.forEach((detail, i) => { detail.hidden = i !== current; });
        dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
        dialog.setAttribute('aria-labelledby', details[current].querySelector('h3').id);
        dialog.setAttribute('aria-describedby', details[current].querySelector('.lt-look__detail-text').id);
        const image = details[current].querySelector('img');
        image.loading = 'eager';
        image.decode().then(() => {
          if (!changing || request !== revision || !dialog.open || closing || reduced()) return;
          image.getAnimations().forEach(animation => animation.cancel());
          image.animate([{ opacity: .85 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
        }).catch(() => {});
      }
      function restore() {
        locks.forEach(([element, overflow]) => { element.style.overflow = overflow; });
        locks = []; closing = false; motion?.cancel(); motion = null;
        opener?.focus({ preventScroll: true });
      }
      async function close() {
        if (!dialog.open || closing) return;
        closing = true; ++revision;
        const appearance = getComputedStyle(sheet);
        const start = { transform: appearance.transform, opacity: appearance.opacity };
        motion?.cancel();
        if (!reduced()) {
          motion = sheet.animate([start, { transform: 'scale(.985)', opacity: 0 }], { duration: 160, easing: 'ease-in', fill: 'forwards' });
          await motion.finished.catch(() => {});
        }
        if (dialog.open) dialog.close();
        restore();
      }
      function open(index, trigger) {
        if (closing || dialog.open) return;
        opener = trigger; show(index); dialog.showModal(); sheet.scrollTop = 0;
        locks = [document.body, document.querySelector('.page-wrapper')].filter(Boolean).map(element => [element, element.style.overflow]);
        locks.forEach(([element]) => { element.style.overflow = 'hidden'; });
        if (!reduced()) {
          motion = sheet.animate([{ transform: 'scale(.98)', opacity: 0 }, { transform: 'none', opacity: 1 }], { duration: 240, easing: 'cubic-bezier(.22,1,.36,1)' });
        }
      }
      triggers.forEach(trigger => { trigger.disabled = false; trigger.addEventListener('click', () => open(Number(trigger.dataset.lookOpen), trigger), options); });
      section.querySelector('[data-look-close]').addEventListener('click', close, options);
      section.querySelector('[data-look-prev]').addEventListener('click', () => show(current - 1), options);
      section.querySelector('[data-look-next]').addEventListener('click', () => show(current + 1), options);
      dots.forEach(dot => dot.addEventListener('click', () => show(Number(dot.dataset.lookDot)), options));
      dialog.addEventListener('click', event => { if (event.target === dialog) close(); }, options);
      dialog.addEventListener('cancel', event => { event.preventDefault(); close(); }, options);
      dialog.addEventListener('close', () => { if (!dialog.open) restore(); }, options);
      dialog.addEventListener('keydown', event => {
        if (closing) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') { event.preventDefault(); show(current + (event.key === 'ArrowRight' ? 1 : -1)); }
      }, options);
      sheet.addEventListener('touchstart', event => {
        if (event.touches.length !== 1 || event.target.closest('button')) { touch = null; return; }
        touch = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }, { ...options, passive: true });
      sheet.addEventListener('touchend', event => {
        if (!touch || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - touch.x, dy = event.changedTouches[0].clientY - touch.y;
        touch = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) show(current + (dx < 0 ? 1 : -1));
      }, { ...options, passive: true });
      sheet.addEventListener('touchcancel', () => { touch = null; }, options);
      instances.set(section, () => { ++revision; controller.abort(); preload?.disconnect(); motion?.cancel(); if (dialog.open) dialog.close(); restore(); instances.delete(section); });
    });
  }
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-lt-look]').forEach(section => instances.get(section)?.()));
})();
